/**
 * The Pages header: the breadcrumb (team, project, then where the open page
 * sits: its folders, or a typed or type page's path) with the tree toggle,
 * All pages, the New control and, on a document, its status and actions.
 * `BreadcrumbHeader` is the bare chrome the pre-connect frame uses too.
 */
import { projectDisplayName } from '../organization/memberDirectory';
import type { ReactNode } from 'react';
import {
  EditorBreadcrumb,
  EditorHeaderBar,
  HeaderIconButton,
  useSharedDocumentBreadcrumb,
  type BreadcrumbCrumb,
  type CollabSidebarCreateMenu,
} from '@nimbalyst/collab-bundle/docs-ui';
import { CopyLinkButton, TrackerItemActionsMenu } from '@nimbalyst/collab-bundle/trackers-ui';
import type { TeamSummary } from '../api';
import { CollabDocumentStatusIndicator, useCollabDocumentStatus } from '../collab/documentStatus';
import { openWebDocumentHistory, useWebDocumentHistoryAvailable } from '../collab/webDocumentHistory';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { orgDocumentPath } from '../routing';
import { PagesCreateMenu } from './PagesCreateMenu';
import type { PagesHeaderPath } from './pagesHeaderPath';

type TeamProject = TeamSummary['projects'][number];

export function DocsBreadcrumbHeader({
  team,
  project,
  projectId,
  documentId,
  folderId,
  onShowHome,
  onSelectFolder,
  treeDocked,
  onDockTree,
  onShowAllPages,
  createMenu,
  pagePath,
  pageHistoryDocumentId,
  onOpenType,
}: {
  team: TeamSummary;
  project: TeamProject;
  projectId: string;
  documentId?: string | null;
  folderId?: string | null;
  /** Set on a typed page or type page: its path stands in for the folder crumbs. */
  pagePath?: PagesHeaderPath | null;
  /** The room a typed page body or type page prose registers its history under. */
  pageHistoryDocumentId?: string | null;
  onOpenType: (typeId: string) => void;
  onShowHome: () => void;
  onSelectFolder: (folderId: string) => void;
  treeDocked: boolean;
  onDockTree: () => void;
  onShowAllPages: () => void;
  createMenu: CollabSidebarCreateMenu | null;
}) {
  const breadcrumb = useSharedDocumentBreadcrumb(documentId, folderId);
  // Connectivity and presence sit on the row that names the document, the way
  // the desktop app arranges them, rather than in a second strip beneath it.
  const status = useCollabDocumentStatus();
  const documentLink = documentId
    ? `${window.location.origin}${orgDocumentPath(team, projectId, documentId)}${window.location.search}${window.location.hash}`
    : null;
  // With no tree beside the content, the crumbs are the way back up: every
  // ancestor folder routes, and only the folder being looked at is inert.
  const currentFolderId = documentId ? null : breadcrumb.folders[breadcrumb.folders.length - 1]?.folderId ?? null;
  return (
    <BreadcrumbHeader
      // Docks the tree as a pane beside the content. Once docked, the pane's
      // own header carries the button that hides it, so this one goes away.
      leading={treeDocked ? undefined : (
        <HeaderIconButton
          className="breadcrumb-tree-toggle size-[26px] text-nim-faint"
          label="Show page tree"
          onClick={onDockTree}
        >
          <DecorativeIcon icon="left_panel_open" size={17} />
        </HeaderIconButton>
      )}
      crumbs={[
        { id: 'team', label: team.name },
        {
          id: 'project',
          label: projectDisplayName(project),
          onClick: onShowHome,
          current: !documentId && !pagePath && breadcrumb.folders.length === 0,
        },
        ...(pagePath ? [
          ...pagePath.crumbs.map((crumb): BreadcrumbCrumb => {
            const { typeId } = crumb;
            return typeId ? { id: crumb.id, label: crumb.label, onClick: () => onOpenType(typeId) } : { id: crumb.id, label: crumb.label };
          }),
          { id: 'page', label: pagePath.title, current: true } satisfies BreadcrumbCrumb,
        ] : []),
        ...(pagePath ? [] : breadcrumb.folders).map((folder): BreadcrumbCrumb => (
          folder.folderId === currentFolderId
            ? { id: `folder-${folder.folderId}`, label: folder.name, current: true }
            : { id: `folder-${folder.folderId}`, label: folder.name, onClick: () => onSelectFolder(folder.folderId) }
        )),
        ...(documentId
          ? [{
            id: 'document',
            label: breadcrumb.documentTitle || 'Opening document…',
            current: true,
          } satisfies BreadcrumbCrumb]
          : []),
      ]}
      actions={(
        <>
          <HeaderIconButton className="pages-all-pages size-[26px] text-nim-faint" label="All pages" onClick={onShowAllPages}>
            <DecorativeIcon icon="list" size={17} />
          </HeaderIconButton>
          <PagesCreateMenu menu={createMenu} />
          {documentId ? <DocumentActions documentId={documentId} status={status} documentLink={documentLink} onShowAllPages={onShowAllPages} /> : null}
          {!documentId && pagePath && pageHistoryDocumentId ? <PageActions historyDocumentId={pageHistoryDocumentId} onShowAllPages={onShowAllPages} /> : null}
        </>
      )}
    />
  );
}

function DocumentActions({
  documentId,
  status,
  documentLink,
  onShowAllPages,
}: {
  documentId: string;
  status: ReturnType<typeof useCollabDocumentStatus>;
  documentLink: string | null;
  onShowAllPages: () => void;
}) {
  // Present once the open page has published its history controller, which
  // only a markdown page does -- the same scope as the desktop.
  const historyAvailable = useWebDocumentHistoryAvailable(documentId);
  return (
        <>
          <CollabDocumentStatusIndicator status={status} />
          {documentLink && <CopyLinkButton value={documentLink} label="Copy link to this document" />}
          <TrackerItemActionsMenu
            label="Document actions"
            actions={[
              ...(documentLink
                ? [{
                  id: 'copy-link',
                  label: 'Copy link',
                  icon: 'link',
                  onSelect: () => { void navigator.clipboard?.writeText(documentLink); },
                }]
                : []),
              ...(status?.desktopHref
                ? [{
                  id: 'open-desktop',
                  label: 'Open in Nimbalyst',
                  icon: 'open_in_new',
                  onSelect: () => { window.location.href = status.desktopHref!; },
                }]
                : []),
              // The desktop app's source-mode escape hatch, in the actions menu
              // this console already has. Present only for a document that can
              // genuinely project to text and back — see `ExtensionEditorMount`
              // — so every other document leaves the menu exactly as it was.
              ...(status?.sourceMode
                ? [{
                  id: 'toggle-source-mode',
                  label: status.sourceMode.active ? 'Show editor' : 'View source',
                  icon: status.sourceMode.active ? 'visibility' : 'code',
                  onSelect: () => { status.sourceMode?.toggle(); },
                }]
                : []),
              ...(historyAvailable
                ? [{
                  id: 'page-history',
                  label: 'Page history',
                  icon: 'history',
                  onSelect: () => openWebDocumentHistory(documentId),
                }]
                : []),
              {
                id: 'all-pages',
                label: 'All pages',
                icon: 'list',
                onSelect: onShowAllPages,
              },
            ]}
          />
        </>
  );
}

/** A typed page or type page: its body's history, once the body has mounted. */
function PageActions({ historyDocumentId, onShowAllPages }: { historyDocumentId: string; onShowAllPages: () => void }) {
  const historyAvailable = useWebDocumentHistoryAvailable(historyDocumentId);
  if (!historyAvailable) return null;
  return (
    <TrackerItemActionsMenu
      label="Page actions"
      actions={[
        { id: 'page-history', label: 'Page history', icon: 'history', onSelect: () => openWebDocumentHistory(historyDocumentId) },
        { id: 'all-pages', label: 'All pages', icon: 'list', onSelect: onShowAllPages },
      ]}
    />
  );
}

/** Breadcrumb chrome shared by the connected and pre-connect docs frames. */
export function BreadcrumbHeader({
  crumbs,
  actions,
  leading,
}: {
  crumbs: readonly BreadcrumbCrumb[];
  actions?: ReactNode;
  leading?: ReactNode;
}) {
  return (
    <EditorHeaderBar
      breadcrumb={<EditorBreadcrumb crumbs={crumbs} leading={leading} />}
      actions={actions}
    />
  );
}
